import React from "react";
import { useLocation, useNavigate } from "react-router-dom";
import html2pdf from "html2pdf.js";
import Logo from "../assest/oakyalogo.png"; // Update the path if necessary
import displayINRCurrency from "../helpers/displayCurrency";

const AllInvoice = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const student = location.state?.student;

  if (!student) {
    return (
      <div className="p-6 text-center">
        <p className="text-red-500 font-semibold">No student data found.</p>
        <button
          onClick={() => navigate("/")}
          className="mt-4 text-blue-600 underline"
        >
          Go back
        </button>
      </div>
    );
  }

  const handleDownloadPDF = () => {
    const element = document.getElementById("invoice-content");

    // Save original style
    const originalStyle = element.getAttribute("style") || "";

    // Temporarily apply scale to fit on single page
    element.style.transform = "scale(0.8)";
    element.style.transformOrigin = "top left";
    element.style.width = "1200px";
    element.style.height = "auto";

    const options = {
      margin: 0.5, // inches
      filename: `Invoice_${student.full_name.replace(/\s/g, "_")}.pdf`,
      image: { type: "jpeg", quality: 0.98 },
      html2canvas: { scale: 2 },
      jsPDF: { unit: "in", format: "letter", orientation: "landscape" },
    };

    html2pdf()
      .set(options)
      .from(element)
      .save()
      .then(() => {
        // Revert the scale to original after export
        element.setAttribute("style", originalStyle);
      })
      .catch((err) => {
        console.error("PDF generation failed:", err);
        element.setAttribute("style", originalStyle); // Always revert
      });
  };

  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    const date = new Date(dateString);
    const day = String(date.getDate()).padStart(2, "0");
    const month = String(date.getMonth() + 1).padStart(2, "0"); // Months are 0-based
    const year = date.getFullYear();
    return `${day}/${month}/${year}`;
  };

  const handlePrint = () => {
    const printStyle = `
    @media print {
      @page {
        margin: 10mm 0mm 10mm 0mm; /* Top, Right, Bottom, Left */
        size: auto;
      }

      html, body {
        margin: 0 !important;
        padding: 0 !important;
      }

      body * {
        visibility: hidden;
      }
      #invoice-content, #invoice-content * {
        visibility: visible;
      }

      #invoice-content {
        position: absolute;
        left: 0;
        top: 0;
        font-size: 12px;
        width: 100% !important;
        padding: 0 5mm;
      }

      #invoice-content .flex.justify-between {
        gap: 4rem !important;
        margin-bottom: 15px !important;
      }

      #invoice-content .flex.justify-between > div {
        width: 45% !important;
      }
    }
  `;

    const styleSheet = document.createElement("style");
    styleSheet.type = "text/css";
    styleSheet.innerText = printStyle;
    document.head.appendChild(styleSheet);

    window.print();

    setTimeout(() => {
      document.head.removeChild(styleSheet); // Cleanup after print
    }, 1000);
  };

  return (
    <div className="p-6 bg-gray-100 min-h-screen">
      {/* Buttons */}
      <div className="print:hidden flex justify-center gap-4 mb-6">
        <button
          onClick={handlePrint}
          className="bg-blue-600 text-white py-2 px-4 rounded hover:bg-blue-700"
        >
          Print
        </button>
        <button
          onClick={handleDownloadPDF}
          className="bg-green-600 text-white py-2 px-4 rounded hover:bg-green-700"
        >
          Download PDF
        </button>
        <button
          onClick={() => navigate(-1)}
          className="bg-gray-600 text-white py-2 px-4 rounded hover:bg-gray-700"
        >
          Back
        </button>
      </div>

      {/* Invoice Copies */}
      <div
        id="invoice-content"
        className="grid grid-cols-1 md:grid-cols-3 gap-4 print:grid-cols-3"
      >
        {["Admin Copy", "Accountant Copy", "Student Copy"].map((copyType) => (
          <div
            key={copyType}
            className="bg-white border border-black rounded-md shadow p-6 text-sm"
          >
            {/* Header */}
            <div className="text-center mb-4">
              <div className="flex items-center justify-center gap-2 mb-2">
                <img src={Logo} alt="Logo" className="h-8" />
                <h1 className="text-lg font-bold">OAKYA FOOD & BEVERAGES</h1>
              </div>
            
            
              <p className="text-black font-semibold mt-1">{copyType}</p>
              <h1 className="text-lg font-bold print:text-sm">
                [AY-2025-2026]
              </h1>
            </div>

            {/* Info */}
            <div className="flex justify-between">
              <div className="mb-4">
                <h2 className="font-semibold mb-2">
                  Date : {formatDate(student.created_date)}
                </h2>
                <p>
                  <strong>PRN No:</strong> {student.student_admission_number}
                </p>
                <p>
                  <strong>Stream:</strong> {student.course}
                </p>
                <p>
                  <strong>Name:</strong> {student.full_name}
                </p>
              </div>
              <div className="mb-4" id="">
                <p>
                  <strong>Invoice Number:</strong> {student.invoice_number}
                </p>
                <p>
                  <strong>Program:</strong>{" "}
                  {[
                    "CSE",
                    "AI & ML",
                    "Aeronautical Engineering",
                    "ECE",
                    "CSE(AI & ML)",
                  ].includes(student.course)
                    ? "B Tech"
                    : "Degree"}
                </p>
                <p>
                  <strong>Year:</strong> {student.year} - {student.semester}
                </p>
              </div>
            </div>

            {/* Table */}
            <div className="mb-4">
              <table className="w-full border border-gray-300 text-xs">
                <thead>
                  <tr>
                    <th rowSpan={2} className="border px-2 py-1 align-middle">
                      1st Semester Fee Details
                    </th>
                    <th colSpan={2} className="border px-2 py-1 text-center">
                      Paid Amount INR
                    </th>
                  </tr>
                  <tr>
                    <th className="border px-2 py-1">Cash</th>
                    <th className="border px-2 py-1">Online</th>
                  </tr>
                </thead>
                <tbody>
                  {[
                    {
                      label: "Fee",
                      value: displayINRCurrency(student.fee),
                    },
                    {
                      label: "Total Amount",
                      value: displayINRCurrency(student.total_amount),
                      isTotal: true,
                    },
                  ].map((item, index) => (
                    <tr
                      key={index}
                      className={
                        item.isTotal ? "bg-gray-100 font-semibold" : ""
                      }
                    >
                      <td className="border px-2 py-1">{item.label}</td>
                      {student.payment_method === "Cash" ? (
                        <>
                          <td className="border px-2 py-1 text-right">
                            {item.value}
                          </td>
                          <td className="border px-2 py-1"></td>
                        </>
                      ) : (
                        <>
                          <td className="border px-2 py-1"></td>
                          <td className="border px-2 py-1 text-right">
                            {item.value}
                          </td>
                        </>
                      )}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <div className="mb-10">
              <p>
                <strong>Payment Terms:</strong> {student.payment_method}
              </p>
              <p>
                <strong>Transaction Id:</strong>{" "}
                {student.transaction_id || "N/A"}
              </p>
              {/* **** START: Remarks Added **** */}
              {student.remarks && (
                <p>
                  <strong>Remarks:</strong> {student.remarks}
                </p>
              )}
              {/* **** END: Remarks Added **** */}
            </div>

            {/* Footer */}
            <div className="text-right text-xs text-gray-600 ml-auto w-fit">
              <p>Authorized Signature</p>
              <p className="font-bold">S.A.V.PRASAD</p>
            </div>

            {/* Notes */}
            <div className="text-gray-600 text-xs mt-4 text-center">
              <p>
                This is the system generated Receipt. Signature is not
                Required.
              </p>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default AllInvoice;